import type { KeyboardEvent } from 'react';
import { DistrictCustomerSiteInfo } from '../../Services/serviceURL';

export interface CustomerLite {
  id: string;
  customerId: string;
  customerName: string;
  soldTo?: string;
  sites?: Site[];
}

export interface Site {
  siteId: string;
  site: string;
  latitude: string;
  longitude: string;
}

export type PlantStatus = 'Active' | 'Inactive';

export interface PlantInfo {
  customer: string;
  site: string;
  plantName: string;
  status: PlantStatus;
  latitude: string;
  longitude: string;
}

export interface Asset {
  id: string;
  type: string;
  name: string;
  status: string;
  isDeleted: boolean;
  parameters: unknown[];
  subAssets: unknown[];
}

export const GUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const EMPTY_PLANT_INFO: PlantInfo = {
  customer: '',
  site: '',
  plantName: '',
  status: 'Active',
  latitude: '',
  longitude: '',
};

export function normalizeSearchString(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .replaceAll(/\s+/g, ' ')
    .trim();
}

export function formatCustomerLabel(customer: CustomerLite): string {
  const soldToSuffix = customer.soldTo ? ` (#${customer.soldTo})` : '';
  return `${customer.customerName}${soldToSuffix}`;
}

export function getCustomerInputDisplayValue(
  isDropdownOpen: boolean,
  searchText: string,
  selectedCustomer: CustomerLite | null
): string {
  if (isDropdownOpen) {
    return searchText;
  }
  if (!selectedCustomer) {
    return '';
  }
  return formatCustomerLabel(selectedCustomer);
}

export function filterBySearchTerm<T>(
  list: T[],
  searchText: string,
  getSearchValues: (item: T) => string[]
): T[] {
  const term = normalizeSearchString(searchText);
  if (!term) {
    return list;
  }

  return list.filter((item) => {
    const values = getSearchValues(item).map(normalizeSearchString);
    return values.some((value) => value === term || value.includes(term));
  });
}

export function transformDistrictCustomers(
  districtCustomerData: DistrictCustomerSiteInfo[]
): CustomerLite[] {
  return districtCustomerData.map((item, index) => {
    const transformedSites: Site[] =
      item.sites?.map((site) => ({
        siteId: site.siteId.toString(),
        site: site.name,
        latitude: '0',
        longitude: '0',
      })) ?? [];

    return {
      id: `customer-${index}`,
      customerId: item.salesDistrictCustomer,
      customerName: item.salesDistrictCustomer,
      soldTo: '',
      sites: transformedSites,
    };
  });
}

export function parseJsonSafely<T>(value: string): T | null {
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

export function resolvePlantGuid(plantId: string): string {
  if (GUID_REGEX.test(plantId)) {
    return plantId;
  }

  const plantGuidFromStorage = localStorage.getItem('currentplantguid') ?? '';
  if (plantGuidFromStorage && GUID_REGEX.test(plantGuidFromStorage)) {
    return plantGuidFromStorage;
  }

  const storedPlantData = localStorage.getItem('currentPlantData');
  if (!storedPlantData) {
    return '';
  }

  const parsedData = parseJsonSafely<{ plantId?: string }>(storedPlantData);
  if (parsedData?.plantId && GUID_REGEX.test(parsedData.plantId)) {
    return parsedData.plantId;
  }

  return '';
}

export function buildPlantInfoFromApiPlant(plant: {
  customer: string;
  site: string;
  plant: string;
  status: string;
  latitude?: string | null;
  longitude?: string | null;
}): PlantInfo {
  return {
    customer: plant.customer,
    site: plant.site,
    plantName: plant.plant,
    status: plant.status as PlantStatus,
    latitude: plant.latitude ?? '',
    longitude: plant.longitude ?? '',
  };
}

export function findCustomerForPlant(
  plantCustomer: string,
  validCustomers: CustomerLite[]
): CustomerLite | undefined {
  let customer = validCustomers.find(
    (entry) =>
      entry.customerId === plantCustomer || entry.customerName === plantCustomer
  );

  if (!customer) {
    const normalizedPlantCustomer = normalizeSearchString(plantCustomer);
    customer = validCustomers.find(
      (entry) =>
        normalizeSearchString(entry.customerId) === normalizedPlantCustomer ||
        normalizeSearchString(entry.customerName) === normalizedPlantCustomer
    );
  }

  customer ??= validCustomers.find(
    (entry) =>
      entry.customerName.includes(plantCustomer) ||
      plantCustomer.includes(entry.customerName)
  );

  return customer;
}

export function findCustomerByPlantData(
  plantData: { customerId?: string; customer?: string },
  customers: CustomerLite[]
): CustomerLite | undefined {
  if (plantData.customerId) {
    const customer = customers.find((entry) => entry.customerId === plantData.customerId);
    if (customer) {
      return customer;
    }
  }

  if (!plantData.customer) {
    return undefined;
  }

  return customers.find(
    (entry) =>
      entry.customerName.includes(plantData.customer!) ||
      plantData.customer!.includes(entry.customerName)
  );
}

export function hasPlantDataChanged(
  originalPlantInfo: PlantInfo | null,
  plantInfo: PlantInfo,
  mode: 'create' | 'edit'
): boolean {
  if (!originalPlantInfo || mode !== 'edit') {
    return false;
  }

  return (
    originalPlantInfo.plantName !== plantInfo.plantName ||
    originalPlantInfo.status !== plantInfo.status ||
    originalPlantInfo.customer !== plantInfo.customer ||
    originalPlantInfo.site !== plantInfo.site ||
    originalPlantInfo.latitude !== plantInfo.latitude ||
    originalPlantInfo.longitude !== plantInfo.longitude
  );
}

export function collectPlantValidationErrors(
  mode: 'create' | 'edit',
  plantInfo: PlantInfo,
  selectedCustomer: CustomerLite | null
): string[] {
  const validationErrors: string[] = [];

  if (mode === 'create' && (!selectedCustomer || !plantInfo.customer)) {
    validationErrors.push('Customer is required');
  }

  if (mode === 'edit' && !plantInfo.customer) {
    validationErrors.push('Customer information is missing');
  }

  if (!plantInfo.site) {
    validationErrors.push('Site information is missing - plantInfo.site is empty');
  }

  if (
    mode === 'create' &&
    plantInfo.site &&
    selectedCustomer?.sites &&
    !selectedCustomer.sites.some((site) => site.site === plantInfo.site)
  ) {
    validationErrors.push(
      `Site '${plantInfo.site}' is not available for customer '${selectedCustomer?.customerName ?? ''}'`
    );
  }

  if (!plantInfo.plantName || plantInfo.plantName.trim() === '') {
    validationErrors.push('Plant name is required');
  }

  return validationErrors;
}

export function buildEditPayload(
  selectedPlantId: string | null,
  plantData: { plantId?: string } | null,
  selectedCustomer: CustomerLite,
  selectedSite: Site,
  plantInfo: PlantInfo
) {
  return {
    id: selectedPlantId || plantData?.plantId || '',
    customerName: selectedCustomer.customerName,
    siteName: selectedSite.site,
    plantName: plantInfo.plantName,
    plantStatus: plantInfo.status,
    cnxSiteId: Number.parseInt(selectedSite.siteId, 10),
    latitude: plantInfo.latitude || '0',
    longitude: plantInfo.longitude || '0',
  };
}

export function extractSaveErrorMessage(err: {
  response?: {
    data?: {
      Message?: string;
      message?: string;
      errors?: Record<string, unknown>;
    };
  };
  message?: string;
}): string {
  let errorMessage = 'Failed to save plant. Please try again.';

  if (err.response?.data?.Message) {
    return err.response.data.Message;
  }

  if (err.response?.data?.errors) {
    const errorMessages: string[] = [];
    for (const [field, messages] of Object.entries(err.response.data.errors)) {
      if (Array.isArray(messages)) {
        errorMessages.push(`${field}: ${messages.join(', ')}`);
      }
    }
    if (errorMessages.length > 0) {
      return `Validation errors: ${errorMessages.join('; ')}`;
    }
  }

  if (err.response?.data?.message) {
    errorMessage = err.response.data.message;
  } else if (err.message) {
    errorMessage = err.message;
  }

  return errorMessage;
}

export function extractEditErrorMessage(payload: unknown): string {
  let errorMessage = 'Failed to update plant. Please try again.';

  if (!payload || typeof payload !== 'object') {
    return errorMessage;
  }

  const error = payload as {
    response?: {
      data?: {
        errors?: Record<string, string[]>;
        title?: string;
        Message?: string;
        message?: string;
      };
    };
    message?: string;
  };

  const errors = error.response?.data?.errors;
  if (errors && typeof errors === 'object') {
    const errorMessages: string[] = [];
    Object.keys(errors).forEach((field) => {
      if (Array.isArray(errors[field])) {
        errors[field].forEach((msg) => {
          errorMessages.push(msg);
        });
      }
    });
    if (errorMessages.length > 0) {
      return errorMessages.join(', ');
    }
  }

  if (error.response?.data?.Message) {
    return error.response.data.Message;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.response?.data?.title) {
    return error.response.data.title;
  }
  if (error.message) {
    return error.message;
  }

  return errorMessage;
}

export function persistPlantApiResponse(plant: {
  plantId: string;
  id: string;
  customerId: string;
}): void {
  localStorage.setItem('currentplantguid', plant.plantId);
  localStorage.setItem('currentPlantId', plant.id);
  localStorage.setItem('customerid', plant.customerId);
}

export function clearPlantStorage(): void {
  localStorage.removeItem('createPlantFormData');
  localStorage.removeItem('currentPlantData');
  localStorage.removeItem('currentPlantId');
  localStorage.removeItem('currentplantguid');
  localStorage.removeItem('customerid');
}

export function triggerPlantListRefresh(): void {
  const refresh = (globalThis as typeof globalThis & { refreshPlantList?: () => void })
    .refreshPlantList;
  refresh?.();
}

export function handleInteractiveKeyDown(
  event: KeyboardEvent,
  action: () => void
): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
}
